/**
 * Purpose:
 * Manages state for ProtectedLayoutClient component.
 *
 * Responsibility:
 * - Menu open/close state
 * - Menu collapsed state and localStorage persistence
 * - Mounted state for transitions
 */

'use client';

import { useState, useCallback } from 'react';

const COLLAPSED_STORAGE_KEY = 'menu-collapsed';

export function useProtectedLayoutState() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // Initialize from localStorage synchronously to prevent layout shift
  // Since this is a 'use client' component, it won't cause hydration mismatch
  const [isMenuCollapsed, setIsMenuCollapsed] = useState(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(COLLAPSED_STORAGE_KEY);
      return stored === 'true';
    }
    return false;
  });

  // Track if component has mounted to disable transition on first render
  const [isMounted, setIsMounted] = useState(false);

  const handleMenuToggle = useCallback(() => {
    setIsMenuOpen((prev) => !prev);
  }, []);

  const handleMenuClose = useCallback(() => {
    setIsMenuOpen(false);
  }, []);

  const handleCollapsedToggle = useCallback(() => {
    setIsMenuCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem(COLLAPSED_STORAGE_KEY, String(next));
      return next;
    });
  }, []);

  const setMounted = useCallback((mounted: boolean) => {
    setIsMounted(mounted);
  }, []);

  return {
    isMenuOpen,
    isMenuCollapsed,
    isMounted,
    handleMenuToggle,
    handleMenuClose,
    handleCollapsedToggle,
    setMounted,
  };
}
